"use client";

import { Skeleton, cn } from "@strivelabs/ui-core";
import { IconButton } from "@strivelabs/ui-core/v2";
import {
  AlertCircleIcon,
  FileIcon,
  FileTextIcon,
  ImageIcon,
  Loader2Icon,
  XIcon,
} from "lucide-react";
import { useEffect, useState, type HTMLAttributes } from "react";

export type AttachmentCardStatus = "uploading" | "ready" | "error";

export type AttachmentCardProps = Omit<HTMLAttributes<HTMLDivElement>, "onClick"> & {
  /** Original filename, shown (truncated) as the card title. */
  filename?: string;
  /** MIME type — drives the icon and the type label (e.g. "PDF"). */
  mediaType?: string;
  /** Loadable URL. Image types render a thumbnail from it; others ignore it. */
  url?: string;
  /** Size in bytes, rendered human-readable in the meta line. */
  size?: number;
  /** Upload lifecycle. Defaults to "ready". */
  status?: AttachmentCardStatus;
  /** Error message shown in the meta line when status is "error". */
  error?: string;
  /** When provided, renders a hover-reveal remove button. */
  onRemove?: () => void;
  /** When provided, the whole card becomes clickable. */
  onClick?: () => void;
  className?: string;
  /** Override the card background (per ui-blocks card conventions). */
  backgroundColor?: string;
};

const BYTES_PER_KB = 1024;
const SIZE_UNITS = [ "Bytes", "KB", "MB", "GB" ];

/**
 * Human-readable byte size. Mirrors the (private) formatter in ui-core's
 * file-upload so attachment chips read the same as the dropzone list. Exported
 * so the composer's drop overlay can render limits in the same units.
 */
export function formatBytes(bytes?: number): string | null {
  if (bytes == null || Number.isNaN(bytes) || bytes < 0) {
    return null;
  }
  if (bytes === 0) {
    return "0 Bytes";
  }
  // Cap the unit index so very large sizes don't overflow SIZE_UNITS and
  // render with the wrong unit (e.g. a TB file as "1 Bytes").
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(BYTES_PER_KB)),
    SIZE_UNITS.length - 1
  );
  const value = Math.round((bytes / BYTES_PER_KB ** i) * 100) / 100;
  return `${value} ${SIZE_UNITS[i]}`;
}

/** Short type label from a MIME type, e.g. "application/pdf" -> "PDF". */
function typeLabel(mediaType?: string): string | null {
  if (!mediaType) {
    return null;
  }
  const subtype = mediaType.split("/")[1] ?? mediaType;
  return subtype.split("+")[0].toUpperCase();
}

function isImage(mediaType?: string): boolean {
  return Boolean(mediaType?.startsWith("image/"));
}

/**
 * Compact attachment preview card used both in the chat composer (compose
 * state, with a remove button) and on rendered/historical messages. Images
 * render a thumbnail from `url`; everything else shows a file-type icon. There
 * is no PDF thumbnailing — PDFs fall back to a document icon card.
 */
export function AttachmentCard({
  filename,
  mediaType,
  url,
  size,
  status = "ready",
  error,
  onRemove,
  onClick,
  className,
  backgroundColor,
  ...props
}: AttachmentCardProps) {
  // Presigned URLs can expire; if the thumbnail fails to load, fall back to
  // the file-type icon instead of a broken image. Reset when the url changes
  // (e.g. history re-fetch mints a fresh presigned URL).
  const [imgFailed, setImgFailed] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  useEffect(() => {
    setImgFailed(false);
    setImgLoaded(false);
  }, [url]);

  const showThumbnail =
    isImage(mediaType) && Boolean(url) && status !== "error" && !imgFailed;
  const Icon = isImage(mediaType)
    ? ImageIcon
    : mediaType === "application/pdf"
      ? FileTextIcon
      : FileIcon;

  const meta =
    status === "uploading"
      ? "Uploading…"
      : status === "error"
        ? error || "Upload failed"
        : [ typeLabel(mediaType), formatBytes(size) ].filter(Boolean).join(" · ") || null;

  const clickable = Boolean(onClick);

  return (
    <div
      className={cn(
        "group/attachment relative flex w-56 max-w-full items-center gap-3 rounded-lg border bg-card p-2 pr-3 text-left",
        clickable && "cursor-pointer transition-colors hover:bg-accent",
        status === "error" && "border-destructive/40",
        className
      )}
      style={backgroundColor ? { backgroundColor } : undefined}
      onClick={onClick}
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      {...(clickable ? { role: "button", tabIndex: 0 } : {})}
      {...props}
    >
      <div className="relative size-10 shrink-0 overflow-hidden rounded-md bg-muted">
        {showThumbnail ? (
          <img
            alt={filename || "attachment"}
            className="size-full object-cover"
            onError={() => setImgFailed(true)}
            onLoad={() => setImgLoaded(true)}
            src={url}
          />
        ) : (
          <div
            className={cn(
              "flex size-full items-center justify-center text-muted-foreground",
              status === "error" && "text-destructive"
            )}
          >
            {status === "error" ? (
              <AlertCircleIcon className="size-5" />
            ) : (
              <Icon className="size-5" />
            )}
          </div>
        )}
        {showThumbnail && !imgLoaded && (
          <Skeleton className="absolute inset-0 size-full rounded-md" />
        )}
        {status === "uploading" && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/60">
            <Loader2Icon className="size-4 animate-spin text-foreground" />
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium text-foreground">
          {filename || "Attachment"}
        </span>
        {meta && (
          <span
            className={cn(
              "truncate text-xs text-muted-foreground",
              status === "error" && "text-destructive"
            )}
          >
            {meta}
          </span>
        )}
      </div>

      {onRemove && (
        <IconButton
          variant="secondary"
          size="2xs"
          aria-label="Remove attachment"
          className="-right-1.5 -top-1.5 absolute opacity-0 group-hover/attachment:opacity-100 focus-visible:opacity-100 group-focus-within/attachment:opacity-100"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          type="button"
        >
          <XIcon className="h-3 w-3" />
        </IconButton>
      )}
    </div>
  );
}
